import React, { useEffect, useRef, useState } from "react";
import api from "../api";
import { CLONE_MODE } from "../utils/cloneCta";

let sdkPromise = null;
let wcsPromise = null;

function loadScript(src, id) {
  if (!src) return Promise.reject(new Error("script URL missing"));
  const existing = document.getElementById(id);
  if (existing && existing.dataset.loaded === "true") return Promise.resolve(existing);
  if (existing) existing.remove(); // 직전 로드 실패로 남은 빈 태그 제거 → 재시도 가능
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => { script.dataset.loaded = "true"; resolve(script); };
    script.onerror = () => { script.remove(); reject(new Error(`script load failed: ${src}`)); };
    document.head.appendChild(script);
  });
}

function getRootDomain() {
  const host = window.location.hostname;
  if (!host || host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return host;
  const parts = host.split(".");
  return parts.length >= 2 ? parts.slice(-2).join(".") : host;
}

export function NaverPayInflowTracker() {
  useEffect(() => {
    let alive = true;
    api.get("/api/naver-pay/config")
      .then(async ({ data }) => {
        if (!alive || !data?.accountId) return;
        if (!wcsPromise) wcsPromise = loadScript("https://wcs.naver.net/wcslog.js", "naver-wcslog-sdk");
        await wcsPromise;
        if (!alive) return;
        window.wcs_add = window.wcs_add || {};
        window.wcs_add.wa = data.accountId;
        if (Array.isArray(data.checkoutWhitelist) && data.checkoutWhitelist.length && window.wcs) {
          window.wcs.checkoutWhitelist = data.checkoutWhitelist;
        }
        if (window.wcs?.inflow) {
          window.wcs.inflow(data.inflowDomain || getRootDomain());
        }
        if (window.wcs_do) window.wcs_do();
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return null;
}

export default function NaverPayButton({
  mode = "product",
  productType = "page",
  productId,
  quantity = 1,
  disabled = false,
  className = "",
  showBenefit = true,
}) {
  const containerIdRef = useRef(`npay-button-${Math.random().toString(36).slice(2)}`);
  const buttonRef = useRef(null);
  const payloadRef = useRef({});
  const [config, setConfig] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    payloadRef.current = {
      mode,
      product_type: productType,
      product_id: productId,
      quantity,
    };
  }, [mode, productType, productId, quantity]);

  useEffect(() => {
    let alive = true;
    api.get("/api/naver-pay/config")
      .then(({ data }) => { if (alive) setConfig(data); })
      .catch(() => { if (alive) setConfig({ enabled: false }); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!config?.enabled || !config.buttonKey || (mode !== "cart" && !productId)) return undefined;
    let alive = true;
    const containerId = containerIdRef.current;

    const renderButton = async () => {
      try {
        if (alive) setFailed(false);
        if (!sdkPromise) sdkPromise = loadScript(config.sdkUrl, "naver-pay-button-sdk");
        try {
          await sdkPromise;
        } catch (loadErr) {
          sdkPromise = null; // 다음 마운트에서 재시도 허용 (영구 오염 방지)
          throw loadErr;
        }
        if (!alive || !window.Npay?.order?.create) return;
        if (buttonRef.current?.dispose) buttonRef.current.dispose();
        buttonRef.current = await window.Npay.order.create({
          buttonKey: config.buttonKey,
          containerId,
          orderRegistrationVersion: config.orderRegistrationVersion || "2.1",
          type: "template",
          colorTheme: "green",
          enable: !disabled,
          components: {
            talkTalk: false,
            wishlist: false,
            benefitMessage: !!showBenefit,
            benefitCoachMark: !!showBenefit,
          },
          onBuyClick: async () => {
            const { data } = await api.post("/api/naver-pay/register-order", payloadRef.current);
            if (!data?.buyKey || !data?.merchantNo) {
              throw new Error("네이버페이 주문등록 응답이 올바르지 않습니다.");
            }
            return { key: data.buyKey, merchantNo: data.merchantNo };
          },
        });
      } catch (err) {
        // SDK 로드 실패(광고차단/네트워크 등)·버튼 생성 실패 → 사용자에겐 빨간 에러 대신 버튼을 숨김
        if (alive) setFailed(true);
      }
    };

    renderButton();

    return () => {
      alive = false;
      if (buttonRef.current?.dispose) buttonRef.current.dispose();
      buttonRef.current = null;
      const el = document.getElementById(containerId);
      if (el) el.innerHTML = "";
    };
  }, [config, disabled, mode, productId, showBenefit]);

  if (CLONE_MODE || !config?.enabled || disabled || failed || (mode !== "cart" && !productId)) return null;

  return (
    <div className={className}>
      <div
        id={containerIdRef.current}
        style={{ minWidth: 200, minHeight: 76, width: "100%" }}
      />
    </div>
  );
}
